import gsap from 'gsap';
import { ScrollTrigger, createScrollTrigger } from 'scripts/lib/gsap/scrollTrigger';
import { SplitText } from 'scripts/lib/gsap/splitText';

import Section from 'scripts/core/section';
import { CampaignsPageSections } from 'common/views/sectionTypes';
import { ChangeColorHeaderConfig, HeaderColorClass, setupTriggerChangeColorHeader } from 'scripts/modules/changeColor';
import { inFrames } from 'scripts/utils/inFrames';
import { Easings } from 'scripts/utils/tweenHelpers';
import { BreakpointType, Breakpoints } from 'common/views/appBreakpoints';
import { setupSection } from 'scripts/utils/setupComponents';

gsap.registerPlugin(ScrollTrigger, SplitText);


const animOffsetY: Record<BreakpointType, number> = {
    [BreakpointType.Desktop]: 160,
    [BreakpointType.Tablet]: 100,
    [BreakpointType.Mobile]: 100,
};

export default class PowerfulSection extends Section {

    splittedAllSplitText: SplitText;

    private get _wrap() { return this.element.querySelector('.power-wrap'); }
    private get _subtitle() { return this.element.querySelector('.power-subtitle'); }
    private get _title() { return this.element.querySelector('.power-title'); }
    private get _content() { return this.element.querySelector('.power-content'); }
    private get _list() { return this.element.querySelector('.power-list'); }
    private get _listItems() { return this.element.querySelectorAll('.power-item'); }
    private get _lastNumber() { return this.element.querySelector('.power-item:last-child .power-item__number'); }
    private get _lastContent() { return this.element.querySelector('.power-item:last-child .power-item__content'); }
    private get _imageWrap() { return this.element.querySelector('.power-image'); }
    private get _images() { return this.element.querySelectorAll('.power-image-item') as unknown as HTMLElement[]; }
    private get _lastImage() { return this._images[this._images.length - 1]; }
    private get _currentBreakpointType() { return Breakpoints.Current.breakpoint.name; }
    private get _allSplitText() { return this.element.querySelectorAll('.split'); }

    private get _lastIndexListItems() { return this._listItems.length - 1; }

    async setupSection() {
        createScrollTrigger({
            trigger: this.element,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
        });


        this.splittedAllSplitText = new SplitText(this._allSplitText, { type: 'lines', linesClass: 'fade-overflow' });

        if (Breakpoints.isDesktop) {
            this._setupTitleScale();
        }

        this._setupChangeColorHeader();
        this._setupInview();
        this._setupListAnimation();
        this._setupImageAnimation();

    }

    private _setupInview() {

        const _currentBreakpointType = Breakpoints.Current.breakpoint.name;
        const _currentOffsetAnimY = animOffsetY[_currentBreakpointType];

        const timelineInView = gsap.timeline()
            .fromTo(this._subtitle, {
                y: _currentOffsetAnimY,
            }, {
                y: 0,
                duration: inFrames(80),
                ease: Easings.customEaseInOut,
            })
            .fromTo(this._title.querySelectorAll('.fade-overflow'), {
                y: _currentOffsetAnimY,
            }, {
                y: 0,
                duration: inFrames(80),
                ease: Easings.customEaseInOut,
                stagger: inFrames(2),
            }, inFrames(5))
            .fromTo(this._listItems, {
                y: _currentOffsetAnimY,
            }, {
                y: 0,
                duration: inFrames(80),
                ease: Easings.customEaseInOut,
                stagger: inFrames(2),
            }, `<${inFrames(2)}`);

        createScrollTrigger({
            trigger: this._wrap,
            start: 'top bottom',
            end: 'top top',
            scrub: 1,
            animation: timelineInView,
        });
    }

    private _setupTitleScale() {
        const timelineScaleTitle = gsap.timeline()
            .to(this._title, { scale: 0.55, duration: inFrames(80), ease: 'Easings.customEaseInOut', transformOrigin: 'center left' });

        createScrollTrigger({
            trigger: this._wrap,
            start: 'top top',
            endTrigger: this._list,
            end: 'top top',
            scrub: 1,
            animation: timelineScaleTitle,
        });
    }

    private _setupChangeColorHeader() {
        const colorConfigWhite: ChangeColorHeaderConfig = {
            element: this.element,
            color: HeaderColorClass.HeroBlack,
            triggerVars: {
                start: 'top top',
                end: 'bottom top',
            },
        };

        setupTriggerChangeColorHeader(colorConfigWhite);
    }

    private _setupImageAnimation() {
        gsap.set(this._images, { autoAlpha: 0 });
        gsap.set(this._images[0], { autoAlpha: 1 });

        const StartPinCharts: Record<BreakpointType, string> = {
            [BreakpointType.Desktop]: 'center center',
            [BreakpointType.Tablet]: 'top center-=40%',
            [BreakpointType.Mobile]: 'top center-=40%',
        };

        const EndPinCharts: Record<BreakpointType, string> = {
            [BreakpointType.Desktop]: 'bottom bottom',
            [BreakpointType.Tablet]: 'bottom-=100 bottom',
            [BreakpointType.Mobile]: 'bottom-=50 bottom',
        };

        createScrollTrigger({
            trigger: this._imageWrap,
            start: StartPinCharts[this._currentBreakpointType],
            endTrigger: this._content,
            end: EndPinCharts[this._currentBreakpointType],
            pin: true,
            pinSpacing: false,
            pinReparent: true,
            scrub: 1,
            fastScrollEnd: true,
        });
    }

    private _setupListAnimation() {

        this._listItems.forEach((item, index) => {
            this._createImageAnimation(item, index);
            this.createTextAnimation(item, index);
        });

    }

    private _createImageAnimation(item: Element, indexEl: number) {

        const tl = gsap.timeline({ defaults: { duration: 1, ease: 'none' } });

        if (indexEl != this._lastIndexListItems) {

            tl
                .fromTo(this._images[indexEl], { scale: 0.9 }, { autoAlpha: 1, scale: 1, duration: 0.2, ease: 'none' })
                .fromTo({ waite: 0 }, { waite: 0 }, { waite: 1, ease: 'none', duration: 0.6 })
                .fromTo(this._images[indexEl], { autoAlpha: 1, scale: 1 }, { autoAlpha: 0, scale: 0.9, duration: 0.2, ease: 'none' });

        } else {
            tl
                .fromTo(this._images[indexEl], { autoAlpha: 0, scale: 0.9 }, { autoAlpha: 1, scale: 1, duration: 0.2, ease: 'none' })
                .fromTo({ waite: 0 }, { waite: 0 }, { waite: 1, ease: 'none', duration: 0.8 });
        }


        createScrollTrigger({
            trigger: item,
            start: 'top-=200 center',
            end: 'bottom-=200 center',
            scrub: 1,
            fastScrollEnd: true,
            animation: tl,
            invalidateOnRefresh: false,
        });
    }

    private createTextAnimation(item: Element, indexEl: number) {

        if (indexEl === this._lastIndexListItems) {
            return;
        }

        const TextAnimationScrollTriggerEnd: Record<BreakpointType, string> = {
            [BreakpointType.Desktop]: 'top top-=200',
            [BreakpointType.Tablet]: 'top top+=200',
            [BreakpointType.Mobile]: 'top top',
        };

        createScrollTrigger({
            trigger: item,
            start: 'top bottom+=200',
            end: TextAnimationScrollTriggerEnd[this._currentBreakpointType],
            scrub: 1,
            fastScrollEnd: true,
            animation: gsap.timeline({ defaults: { duration: 1 } })
                .fromTo(this._listItems[indexEl], {
                    autoAlpha: 0.2,
                }, {
                    autoAlpha: 1,
                    duration: 0.3,
                    ease: 'none',
                }, 0.2)
                .to(this._listItems[indexEl], {
                    autoAlpha: 0,
                    duration: 0.1,
                    ease: 'none',
                }, 0.9),
        });
    }
}

setupSection(PowerfulSection, { id: CampaignsPageSections.Powerful });
